/**
 * Verify OTP Step (V2)
 *
 * 6-digit OTP entry. Uses native one-time-code autofill on iOS/Android.
 * Auto-advances between boxes; auto-submits when 6 digits are entered.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface VerifyOtpStepProps {
  email: string;
  onSubmit: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  onChangeEmail: () => void;
  loading: boolean;
  error: string | null;
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;

export const VerifyOtpStep: React.FC<VerifyOtpStepProps> = ({
  email,
  onSubmit,
  onResend,
  onChangeEmail,
  loading,
  error,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const setDigit = (idx: number, value: string) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 1);
    setDigits(prev => {
      const next = [...prev];
      next[idx] = sanitized;
      return next;
    });
    if (sanitized && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
    if (pasted.length === OTP_LENGTH) {
      // tiny defer so state has flushed
      setTimeout(() => onSubmit(pasted), 0);
    }
  };

  // Auto-submit on full entry
  useEffect(() => {
    const otp = digits.join('');
    if (otp.length === OTP_LENGTH && !loading) {
      onSubmit(otp);
    }
    // We intentionally do NOT include onSubmit in deps to avoid double-fires
    // when the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits.join('')]);

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
      setDigits(Array(OTP_LENGTH).fill(''));
      setResendCooldown(RESEND_COOLDOWN_S);
      inputsRef.current[0]?.focus();
    } finally {
      setResending(false);
    }
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-slate-900'>Enter verification code</h2>
      <p className='mb-6 text-slate-500'>
        We sent a 6-digit code to{' '}
        <span className='font-medium text-slate-900'>{email}</span>. It expires in 10 minutes.
      </p>

      <div
        className='mb-4 flex justify-between gap-2 sm:justify-start sm:gap-3'
        role='group'
        aria-label='One-time code'
      >
        {digits.map((d, idx) => (
          <input
            key={idx}
            ref={el => { inputsRef.current[idx] = el; }}
            type='text'
            inputMode='numeric'
            // Only the first input gets the autocomplete hint; mobile autofill
            // will populate all 6 from the SMS/email when triggered.
            autoComplete={idx === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={d}
            onChange={e => setDigit(idx, e.target.value)}
            onKeyDown={e => handleKeyDown(idx, e)}
            onPaste={handlePaste}
            disabled={loading}
            aria-label={`Digit ${idx + 1}`}
            className='h-12 w-10 rounded-lg border border-slate-300 text-center text-lg font-semibold text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 disabled:opacity-50 sm:h-14 sm:w-12 sm:text-xl'
          />
        ))}
      </div>

      {error && (
        <div className='mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
          {error}
        </div>
      )}

      {loading && (
        <div className='mb-4 flex items-center gap-2 text-sm text-slate-500'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Verifying code...
        </div>
      )}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={onChangeEmail}
          className='-ml-2 text-slate-600 hover:text-slate-900'
        >
          <ArrowLeft className='mr-1 h-4 w-4' />
          Use a different email
        </Button>

        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={handleResend}
          disabled={resendCooldown > 0 || resending}
          className='text-teal-700 hover:text-teal-800'
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
          {resendCooldown > 0
            ? `Resend in ${resendCooldown}s`
            : 'Resend code'}
        </Button>
      </div>
    </div>
  );
};
